import React, { createContext, useMemo } from 'react';
import { Permissions } from '../services';

export interface PathNode {
  id: number;
  name: string;
}

export interface ActiveFolderMetadata {
  id: number;
  name: string;
  permissions: Permissions;
  shareable_link: string;
}

export interface ActiveFolder {
  id: number;
  path: PathNode[];
  metadata: ActiveFolderMetadata;
}

interface FileExplorerContextTypes {
  activeFolder: ActiveFolder | null;
  setActiveFolder: (folder: ActiveFolder | null) => void;
  expandedFolderIds: Set<number>;
  toggleFolderExpanded: (folderId: number) => void;
  expandFolders: (folderIds: number[]) => void;
  newlyAddedItems: Set<number>;
  addNewlyAddedItems: (itemIds: number[]) => void;
  removeNewlyAddedItem: (itemId: number) => void;
  userIsSuperuser: boolean;
}

// Initialize with proper default values that satisfy the interface
export const FileExplorerContext = createContext<FileExplorerContextTypes>({
  activeFolder: null,
  setActiveFolder: () => {},
  expandedFolderIds: new Set(),
  toggleFolderExpanded: () => {},
  expandFolders: () => {},
  newlyAddedItems: new Set(),
  addNewlyAddedItems: () => {},
  removeNewlyAddedItem: () => {},
  userIsSuperuser: false,
});

/**
 * FileExplorerContextProvider manages the state of the currently selected file or folder
 * in the file explorer. It provides this selection state and a setter function to all
 * child components, allowing them to read or update the selected node throughout the
 * file explorer component tree without prop drilling.
 *
 * Note: Context stores only IDs and paths. Actual folder data should be
 * fetched via React Query using the activeFolderId from context.
 */
export function FileExplorerContextProvider(
  { children, userIsSuperuser }: { children: React.ReactNode, userIsSuperuser: boolean }) {
  const [activeFolder, setActiveFolder] = React.useState<ActiveFolder | null>(null);
  const [expandedFolderIds, setExpandedFolderIds] = React.useState<Set<number>>(new Set());
  const [newlyAddedItems, setNewlyAddedItems] = React.useState<Set<number>>(new Set());

  const toggleFolderExpanded = React.useCallback((folderId: number) => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  const expandFolders = React.useCallback((folderIds: number[]) => {
    setExpandedFolderIds((prev) => new Set([...prev, ...folderIds]));
  }, []);

  const removeNewlyAddedItem = React.useCallback((itemId: number) => {
    setNewlyAddedItems((prev) => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);
  const addNewlyAddedItems = React.useCallback((itemIds: number[]) => {
    setNewlyAddedItems((prev) => new Set([...prev, ...itemIds]));
    itemIds.forEach((itemId) => {
      setTimeout(() => {
        removeNewlyAddedItem(itemId);
      }, 3000);
    });
  }, [removeNewlyAddedItem]);

  const values = useMemo(() => ({
    activeFolder,
    setActiveFolder,
    expandedFolderIds,
    toggleFolderExpanded,
    expandFolders,
    userIsSuperuser,
    newlyAddedItems,
    addNewlyAddedItems,
    removeNewlyAddedItem,
  }), [activeFolder,
    expandedFolderIds,
    toggleFolderExpanded,
    expandFolders,
    userIsSuperuser,
    newlyAddedItems,
    addNewlyAddedItems,
    removeNewlyAddedItem,
  ]);

  return (
    <FileExplorerContext.Provider value={values}>
      {children}
    </FileExplorerContext.Provider>
  );
}
